import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiMail,
  FiUserCheck,
  FiUserX,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiSearch,
  FiRefreshCw,
  FiSend,
  FiClock,
} from "react-icons/fi";
import {
  getInviteCounts,
  getReceivedContactInvites,
  getReceivedNetworkInvites,
  acceptNetworkInvite,
  denyNetworkInvite,
} from "../../../api/customer/customerFeatureApis";

const getInitials = (firstName = "", lastName = "") => {
  const f = firstName ? firstName.charAt(0) : "";
  const l = lastName ? lastName.charAt(0) : "";
  return (f + l).toUpperCase() || "IN";
};

export default function CustomerInvitationsHub() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.token || authState?.token || authState?.user?.refreshToken;

  const [activeTab, setActiveTab] = useState("contact"); // "contact" | "network"
  const [counts, setCounts] = useState({ contactCount: 0, networkCount: 0, totalCount: 0 });
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [alertMsg, setAlertMsg] = useState(null);

  // Fetch counts
  const fetchCounts = useCallback(async () => {
    if (!token) return;
    try {
      const countsData = await getInviteCounts(token);
      setCounts(countsData);
    } catch (err) {
      console.error("Failed to load invite counts:", err);
    }
  }, [token]);

  // Fetch invites for current tab
  const fetchInvites = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setAlertMsg(null);
    try {
      let data;
      if (activeTab === "contact") {
        data = await getReceivedContactInvites(token);
      } else {
        data = await getReceivedNetworkInvites(token);
      }
      const rawList = data?.inviteRequests || data?.users || [];
      setInvites(rawList);
    } catch (err) {
      console.error("Failed to load invites:", err);
      setAlertMsg({ type: "danger", text: "Failed to load invitations. Please refresh." });
    } finally {
      setLoading(false);
    }
  }, [activeTab, token]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  // Accept Invite
  const handleAccept = async (inviteId) => {
    setActionLoadingId(inviteId);
    try {
      const res = await acceptNetworkInvite(token, inviteId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: res.text || "Invitation accepted successfully!" });
        setInvites((prev) => prev.filter((i) => i.id !== inviteId));
        fetchCounts();
      } else {
        setAlertMsg({ type: "danger", text: res?.text || "Could not accept invitation." });
      }
    } catch (err) {
      setAlertMsg({
        type: "danger",
        text: err?.response?.data?.text || "Failed to accept invitation.",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Deny Invite
  const handleDeny = async (inviteId) => {
    setActionLoadingId(inviteId);
    try {
      const res = await denyNetworkInvite(token, inviteId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: res.text || "Invitation declined." });
        setInvites((prev) => prev.filter((i) => i.id !== inviteId));
        fetchCounts();
      } else {
        setAlertMsg({ type: "danger", text: res?.text || "Could not decline invitation." });
      }
    } catch (err) {
      setAlertMsg({
        type: "danger",
        text: err?.response?.data?.text || "Failed to decline invitation.",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredInvites = invites.filter((item) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const sender = item?.sender || {};
    const name = `${sender.first_name || ""} ${sender.last_name || ""}`.toLowerCase();
    const email = (sender.email_address || "").toLowerCase();
    const service = (sender.service_name || "").toLowerCase();
    return name.includes(q) || email.includes(q) || service.includes(q);
  });

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2 text-dark">
            <FiMail className="text-primary" /> Invitations & Collaboration Requests
          </h3>
          <p className="text-muted mb-0 small">
            Review and respond to pending connection and network partnership invitations.
          </p>
        </div>
        <button
          className="btn btn-outline-secondary d-flex align-items-center gap-1 btn-sm"
          onClick={() => {
            fetchCounts();
            fetchInvites();
          }}
          disabled={loading}
        >
          <FiRefreshCw className={loading ? "spin-icon" : ""} /> Refresh
        </button>
      </div>

      {/* Global Alert Notification */}
      {alertMsg && (
        <div
          className={`alert alert-${alertMsg.type} alert-dismissible fade show d-flex align-items-center justify-content-between mb-3`}
          role="alert"
        >
          <span>{alertMsg.text}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setAlertMsg(null)}
          />
        </div>
      )}

      {/* Metric Cards Banner */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6">
          <div
            className={`card border-0 shadow-sm p-3 rounded-3 cursor-pointer ${
              activeTab === "contact" ? "border-start border-primary border-4" : ""
            }`}
            style={{ background: "#f8faff", transition: "all 0.2s" }}
            onClick={() => setActiveTab("contact")}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted small fw-medium">Contact Requests</span>
                <h4 className="fw-bold text-primary mb-0 mt-1">{counts.contactCount}</h4>
              </div>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary"
                style={{ width: "46px", height: "46px" }}
              >
                <FiUsers size={22} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div
            className={`card border-0 shadow-sm p-3 rounded-3 cursor-pointer ${
              activeTab === "network" ? "border-start border-success border-4" : ""
            }`}
            style={{ background: "#f6fdf9", transition: "all 0.2s" }}
            onClick={() => setActiveTab("network")}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted small fw-medium">Network Collaboration Invites</span>
                <h4 className="fw-bold text-success mb-0 mt-1">{counts.networkCount}</h4>
              </div>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success"
                style={{ width: "46px", height: "46px" }}
              >
                <FiSend size={22} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs and Search Bar */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-6">
              <ul className="nav nav-pills">
                <li className="nav-item">
                  <button
                    className={`nav-link btn-sm fw-medium px-3 me-2 ${
                      activeTab === "contact" ? "active" : "text-secondary"
                    }`}
                    onClick={() => setActiveTab("contact")}
                  >
                    Contact Invites ({counts.contactCount})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link btn-sm fw-medium px-3 ${
                      activeTab === "network" ? "active" : "text-secondary"
                    }`}
                    onClick={() => setActiveTab("network")}
                  >
                    Network Invites ({counts.networkCount})
                  </button>
                </li>
              </ul>
            </div>
            <div className="col-12 col-md-6">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Filter by sender name, role, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invitations List */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading invitations...</span>
          </div>
          <p className="text-muted mt-2 small">Loading pending invitations...</p>
        </div>
      ) : filteredInvites.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
          <div className="card-body">
            <FiMail size={48} className="text-muted mb-3 opacity-50" />
            <h5 className="fw-semibold text-secondary">No Invitations Found</h5>
            <p className="text-muted small mb-0">
              {searchQuery
                ? `No invitations match "${searchQuery}".`
                : activeTab === "contact"
                ? "You do not have any pending contact requests at the moment."
                : "You do not have any pending network collaboration invites at the moment."}
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {filteredInvites.map((item) => {
            const sender = item.sender || {};
            const fullName = `${sender.first_name || ""} ${sender.last_name || ""}`.trim() || "User";
            const initials = getInitials(sender.first_name, sender.last_name);
            const isBusy = actionLoadingId === item.id;
            const photoUrl = sender.photo
              ? `https://images.allhomedesire.com${sender.photo}`
              : null;

            return (
              <div key={item.id} className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm rounded-3 p-3 h-100 d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-start gap-3">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={fullName}
                        className="rounded-circle object-fit-cover shadow-sm"
                        style={{ width: "52px", height: "52px" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="rounded-circle bg-primary bg-opacity-10 text-primary fw-bold d-flex align-items-center justify-content-center shadow-sm"
                      style={{
                        width: "52px",
                        height: "52px",
                        fontSize: "16px",
                        display: photoUrl ? "none" : "flex",
                      }}
                    >
                      {initials}
                    </div>

                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="fw-bold text-dark mb-0">{fullName}</h6>
                        <span className="badge bg-light text-muted border" style={{ fontSize: "11px" }}>
                          {activeTab === "contact" ? "Contact" : "Partner"}
                        </span>
                      </div>

                      {sender.service_name && (
                        <div className="text-primary small fw-medium mt-1">
                          {sender.service_name}
                        </div>
                      )}

                      {sender.email_address && (
                        <div className="text-muted small">{sender.email_address}</div>
                      )}

                      <div className="bg-light p-2 rounded-2 mt-2 text-secondary small">
                        "I'd like to connect and explore collaboration on{" "}
                        {sender.service_name ? sender.service_name.toLowerCase() : "our services"}."
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2 justify-content-end pt-3 mt-3 border-top">
                    <button
                      className="btn btn-outline-danger btn-sm px-3 d-flex align-items-center gap-1"
                      onClick={() => handleDeny(item.id)}
                      disabled={isBusy}
                    >
                      <FiXCircle size={15} /> {isBusy ? "Processing..." : "Decline"}
                    </button>
                    <button
                      className="btn btn-primary btn-sm px-3 d-flex align-items-center gap-1 shadow-sm"
                      onClick={() => handleAccept(item.id)}
                      disabled={isBusy}
                    >
                      <FiCheckCircle size={15} /> {isBusy ? "Processing..." : "Accept"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
